import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { ApiError } from '@schooly/api';
import { useUserContext } from '@schooly/components/authentication';
import { DuplicatesRow } from '@schooly/components/duplicates';
import { scrollToErrorInput } from '@schooly/components/filters';
import { ControlTextField } from '@schooly/components/form-text-field';
import { useNotifications } from '@schooly/components/notifications';
import {
  AREA_CODE_OPTIONS,
  LANGUAGE_OPTIONS,
  RELATIONSHIP_TYPE_OPTIONS,
  USER_NATIONALITIES_MAX_COUNT,
  VALID_EMAIL_REGEXP,
} from '@schooly/constants';
import { GENDER_OPTIONS, NATIONALITY_OPTIONS, TITLE_OPTIONS } from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Spin,
} from '@schooly/style';
import {
  convertEmailInUseRelationToParentForm,
  emailIsDuplicatedInApplication,
  getCustomFieldsWithEmptyValue,
  getEmailInUseForSchoolErrorTextId,
  getEmailInUsePublicErrorTextId,
} from '@schooly/utils/application-helpers';
import { getUserFullNameWithTitle } from '@schooly/utils/get-user-full-name-with-title';

import FormSelect2 from 'apps/web/src/components/ui/Input/FormSelect2';

import { FormRadioGroup } from 'apps/web/src/components/uikit-components/FormCheckbox/FormRadioGroup';
import { FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { CreateParentForm } from './CreateParentContent.type';
import { CustomFieldsFormContainer } from './CustomFieldsFormContainer';
import { ErrorEmailBlock } from './ErrorEmailBlock';
import { EmailInUseValidation } from './hooks/useSchoolEmailInUseValidation';
import { RemovePersonConfirmDialog } from './RemovePersonConfirmDialog';
import { RowsContainer } from './RowsContainer';

interface CreateParentContentProps {
  onSubmit: (parent: CreateParentForm) => void;
  onRemoveParent: (parentId: string) => void;
  parent?: CreateParentForm;
  formCustomFields: CreateParentForm['custom_fields'];
  onClose: () => void;
  schoolId: string;
  onAddExistingAdult: (adult: CreateParentForm) => void;
  parents: CreateParentForm[];
  emailValidation: EmailInUseValidation;
  disableEmail?: boolean;
  canDelete?: boolean;
  onLoginClick?: () => void;
}

export const CreateParentContent: FC<CreateParentContentProps> = ({
  onSubmit,
  onRemoveParent,
  parent,
  formCustomFields,
  onClose,
  schoolId,
  onAddExistingAdult,
  parents,
  emailValidation,
  disableEmail = false,
  canDelete = true,
  onLoginClick,
}) => {
  const { formatMessage } = useIntl();
  const { user } = useUserContext();
  const { showError } = useNotifications();
  const [isConfirmationDialogOpen, showConfirmationDialog, hideConfirmationDialog] = useFlag();
  const { isValidating, checkUserEmailInUse, emailOwner, emailOwnerType, checkEmailInUseType } =
    emailValidation;
  const canLogin = checkEmailInUseType === 'public' && !user && !!onLoginClick;
  const emailInUseErrorTextId =
    checkEmailInUseType === 'school'
      ? getEmailInUseForSchoolErrorTextId(!!emailOwner)
      : getEmailInUsePublicErrorTextId(canLogin);

  const defaultValues: CreateParentForm = parent ?? {
    id: uuidv4(),
    relationship_type: undefined,
    profile_id: null,
    profession: '',
    last_name: '',
    given_name: '',
    email: '',
    nationalities: [],
    telephone: '',
    phoneCode: '',
    can_be_edited: true,
    primary: false,
    custom_fields: formCustomFields,
  };

  const form = useForm<CreateParentForm>({ defaultValues, shouldFocusError: false });

  const title = form.watch('title');
  const givenName = form.watch('given_name');
  const lastName = form.watch('last_name');
  const nationalities = form.watch('nationalities') ?? [];

  const hasEmailInUseError = form.formState.errors.email?.type === 'email-in-use';

  const modalHeaderTitle = useMemo(() => {
    if (!title && !givenName && !lastName) {
      return formatMessage({
        id: 'applications-Parent-AddParentInfo',
      });
    }
    return getUserFullNameWithTitle({ title, last_name: lastName ?? '', given_name: givenName });
  }, [formatMessage, givenName, lastName, title]);

  const handleEmailChange = useCallback(() => {
    if (hasEmailInUseError) {
      form.clearErrors('email');
    }
  }, [form, hasEmailInUseError]);

  const handleAddExistingAdult = useCallback(() => {
    if (!emailOwner) return;
    const customFields = getCustomFieldsWithEmptyValue(formCustomFields);
    const parent = convertEmailInUseRelationToParentForm(emailOwner, customFields);
    onAddExistingAdult(parent);
    onClose();
  }, [emailOwner, formCustomFields, onAddExistingAdult, onClose]);

  const validateEmail = useCallback(
    (email?: string) => {
      if (!email) return true;
      const isDuplicated = emailIsDuplicatedInApplication({
        email,
        parentId: parent?.id ?? '',
        allParents: parents,
      });

      if (isDuplicated) {
        return formatMessage({ id: 'applications-ErrorEmail-EmailUsedInApplication' });
      }

      return true;
    },
    [formatMessage, parent?.id, parents],
  );

  const handleSubmit = useCallback(
    async (data: CreateParentForm) => {
      if (!data.email) return;

      try {
        const { email_in_use } = await checkUserEmailInUse({
          email: data.email,
          schoolId,
        });

        if (email_in_use) {
          form.setError('email', { type: 'email-in-use' });
          return;
        }

        onSubmit(data);
      } catch (error) {
        showError(error as ApiError);
      }
    },
    [checkUserEmailInUse, form, onSubmit, schoolId, showError],
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToErrorInput)}>
        <ModalHeader active title={modalHeaderTitle}>
          <IconButton onClick={onClose}>
            <CrossIcon />
          </IconButton>
        </ModalHeader>
        <ModalMain>
          <ModalContent active>
            <Stack gap={4}>
              <Stack gap={2}>
                <Stack pb={1}>
                  <FormSelect2
                    rules={{ required: true }}
                    name="relationship_type"
                    labelTextId="relationship"
                    options={RELATIONSHIP_TYPE_OPTIONS}
                  />
                </Stack>

                <Typography variant="h4" color="text.secondary">
                  <FormattedMessage id="applications-PersonalInformation" />
                </Typography>

                <Stack direction="row" gap={1.25}>
                  <Box flex={1}>
                    <FormSelect2
                      name="title"
                      labelTextId="peopleDetail-Title"
                      options={TITLE_OPTIONS}
                    />
                  </Box>

                  <Box flex={2}>
                    <ControlTextField
                      name="given_name"
                      control={form.control}
                      rules={{ required: true }}
                      label={formatMessage({ id: 'peopleDetail-GivenNames' })}
                      canClear
                      fullWidth
                      data-test-id="parent-given-name-block"
                    />
                  </Box>
                </Stack>

                <ControlTextField
                  name="last_name"
                  control={form.control}
                  rules={{ required: true }}
                  label={formatMessage({ id: 'peopleDetail-FamilyName' })}
                  canClear
                  fullWidth
                  data-test-id="parent-last-name-block"
                />

                <FormRadioGroup options={[...GENDER_OPTIONS].reverse()} name="gender" />

                <FormSelect2
                  name="nationalities"
                  labelTextId={
                    nationalities.length <= 1
                      ? 'peopleDetail-Nationality'
                      : 'peopleDetail-Nationality-plural'
                  }
                  options={NATIONALITY_OPTIONS}
                  rules={{ required: true }}
                  multiple
                  maxCount={USER_NATIONALITIES_MAX_COUNT}
                />

                <FormSelect2
                  name="language"
                  labelTextId="peopleDetail-PrimaryLanguage"
                  options={LANGUAGE_OPTIONS}
                />

                <ControlTextField
                  name="profession"
                  control={form.control}
                  label={formatMessage({ id: 'peopleDetail-Profession' })}
                  canClear
                  fullWidth
                />
              </Stack>

              <Stack gap={2}>
                <Typography variant="h4" color="text.secondary">
                  <FormattedMessage id="applications-ContactInformation" />
                </Typography>

                <Stack direction="row" gap={1.25}>
                  <Box flex={1}>
                    <FormSelect2
                      name="phoneCode"
                      labelTextId="AreaCode"
                      options={AREA_CODE_OPTIONS}
                      noRequiredLabel
                      showSelectedValue
                      optionClassName="CountryCodeSelectOption"
                    />
                  </Box>

                  <Box flex={2}>
                    <ControlTextField
                      name="telephone"
                      control={form.control}
                      label={formatMessage({ id: 'peopleDetail-PhoneNumber' })}
                      canClear
                      fullWidth
                    />
                  </Box>
                </Stack>

                <Stack gap={1}>
                  <ControlTextField
                    name="email"
                    control={form.control}
                    onChange={handleEmailChange}
                    label={formatMessage({ id: 'peopleDetail-EmailAddressPersonal' })}
                    canClear={!isValidating && !disableEmail}
                    fullWidth
                    //TODO: Refactor email validation - TR-4353
                    rules={{
                      required: true,
                      pattern: {
                        value: VALID_EMAIL_REGEXP,
                        message: formatMessage({
                          id: 'input-ErrorInvalidEmail',
                        }),
                      },
                      validate: validateEmail,
                    }}
                    data-test-id="parent-email-block"
                    disabled={disableEmail}
                  />

                  {hasEmailInUseError && (
                    <ErrorEmailBlock
                      textId={emailInUseErrorTextId}
                      renderAction={
                        canLogin
                          ? () => (
                              <Button onClick={onLoginClick}>
                                {formatMessage({ id: 'signUp-Login' })}
                              </Button>
                            )
                          : undefined
                      }
                    >
                      {emailOwner && emailOwnerType && (
                        <DuplicatesRow
                          duplicate={{ ...emailOwner, avatar: emailOwner.avatar ?? undefined }}
                          userType={emailOwnerType}
                          endAction={
                            <Button onClick={handleAddExistingAdult}>
                              {formatMessage({ id: 'applications-AddExistingParent' })}
                            </Button>
                          }
                        />
                      )}
                    </ErrorEmailBlock>
                  )}
                </Stack>
              </Stack>

              {!!formCustomFields?.length && (
                <Stack pt={1}>
                  <RowsContainer
                    color="text.secondary"
                    containerTitle="applications-AdditionalInformation"
                  >
                    <CustomFieldsFormContainer />
                  </RowsContainer>
                </Stack>
              )}
            </Stack>
          </ModalContent>
        </ModalMain>

        <ModalFooter
          active
          sx={{ justifyContent: parent && canDelete ? 'space-between' : 'flex-end' }}
        >
          {parent && canDelete && (
            <Button variant="outlined" startIcon={<DeleteIcon />} onClick={showConfirmationDialog}>
              <FormattedMessage id="action-Delete" />
            </Button>
          )}
          <Button
            type="submit"
            endIcon={isValidating ? <Spin /> : <CheckIcon />}
            disabled={isValidating}
          >
            <FormattedMessage id="action-Save" />
          </Button>
        </ModalFooter>

        <RemovePersonConfirmDialog
          isOpen={isConfirmationDialogOpen}
          onClose={hideConfirmationDialog}
          onConfirm={() => parent && onRemoveParent(parent.id)}
          personName={parent ? getUserFullNameWithTitle(parent) : ''}
        />
      </form>
    </FormProvider>
  );
};
